import { useMemo, useState, useEffect, Fragment } from "react";
import {
  useExpanded,
  useTable,
  useFilters,
  useGlobalFilter,
  useAsyncDebounce,
  usePagination,
  useFlexLayout,
} from "react-table";
import RenderAssets from "../RenderAsset/RenderAssets";
import { Input, Icon, Loader } from "semantic-ui-react";
import CampaignButtonLink from "../CampaignButtonLink/CampaignButtonLink";

// Define a default UI for filtering
export const GlobalFilter = ({
  defaultGlobalFieldValue,
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  setGlobalFilterValue,
  hasFilters,
  isRemovingFilters,
  isLazyLoading,
  t,
}) => {
  const count = preGlobalFilteredRows.length;
  const [, setValue] = useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value);
  }, 200);

  useEffect(() => {
    setValue(globalFilter);
    setGlobalFilter(globalFilter);
  }, [globalFilter, hasFilters, isRemovingFilters]);

  if (isRemovingFilters) {
    return (
      <Input
        key={123}
        icon="search"
        disabled={isLazyLoading}
        placeholder={`${t("search")} ${t("record_count", { count })}...`}
        style={{
          fontSize: "1.1rem",
          border: "0",
          marginBottom: "2.5rem",
          display: "flex",
          marginLeft: "auto",
          width: "230px",
          height: "36px",
        }}
      />
    );
  } else {
    return (
      <Input
        key={456}
        icon="search"
        disabled={isLazyLoading}
        value={defaultGlobalFieldValue}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
          setGlobalFilterValue && setGlobalFilterValue(e.target.value);
        }}
        placeholder={`${t("search")} ${t("record_count", { count })}...`}
        style={{
          fontSize: "1.1rem",
          border: "0",
          marginBottom: "2.5rem",
          display: "flex",
          marginLeft: "auto",
          width: "230px",
          height: "36px",
        }}
      />
    );
  }
};

// Define a default UI for filtering
export const DefaultColumnFilter = ({
  column: { filterValue, preFilteredRows, setFilter, id },
  onFilterChange,
}) => {
  return (
    <Input
      value={filterValue || ""}
      onChange={(e) => {
        onFilterChange && onFilterChange(e.target.value, id);
        setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
      }}
      style={{ maxWidth: "145px", width: "100%" }}
    />
  );
};

// This is a custom filter UI for selecting
// a unique option from a list
export const SelectColumnFilter = ({
  column: { filterValue, setFilter, preFilteredRows, id },
  onFilterChange,
  options: providedOptions,
}) => {
  const options = useMemo(() => {
    if (Array.isArray(providedOptions) && providedOptions.length > 0) {
      return providedOptions.map((option) =>
        typeof option === "object"
          ? option
          : {
              value: option,
              label: option,
            }
      );
    }

    const uniqueOptions = new Set();
    preFilteredRows.forEach((row) => {
      const value = row.values[id];
      if (value !== undefined && value !== null && value !== "") {
        uniqueOptions.add(value);
      }
    });

    return Array.from(uniqueOptions).map((option) => ({
      value: option,
      label: option,
    }));
  }, [id, preFilteredRows, providedOptions]);

  return (
    /* The following eslint warning is disabled since using onBlur doesn't achieve the
       desired funcionality:
       "onBlur must be used instead of onchange, unless absolutely necessary 
       and it causes no negative consequences for keyboard only or screen reader users." 
    */
    /* eslint-disable-next-line jsx-a11y/no-onchange */
    <select
      value={filterValue || ""}
      onChange={(e) => {
        onFilterChange && onFilterChange(e.target.value, id);
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">All</option>
      {options.map((option) => (
        <option key={String(option.value)} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

function SubRows({ row, rowProps, visibleColumns, data, loading, renderAssets, t }) {
  if (loading) {
    return (
      <tr className="expanded-child">
        <td colSpan={visibleColumns?.length || 1}>
          <Loader active={loading} inline="centered" size="small">
            {t("loading")}
          </Loader>
        </td>
      </tr>
    );
  }
  // error handling here :)
  return (
    <>
      {data.map((x, i) => {
        return (
          <tr
            {...rowProps}
            key={`${rowProps.key}-expanded-${i}`}
            className={row.isExpanded ? "expanded-child" : ""}
          >
            {renderAssets ? (
              <td>
                <CampaignButtonLink campaignId={row.original.id} t={t} />
                {row.original.assets.length > 0 && (
                  <RenderAssets assets={row.original.assets} t={t} />
                )}
              </td>
            ) : (
              row.cells.map((cell, n) => {
                return (
                  <td key={n} {...cell.getCellProps()}>
                    {cell.render(cell.column.SubCell ? "SubCell" : "Cell", {
                      value: cell.column.accessor && cell.column.accessor(x, i),
                      row: { ...row, original: x },
                    })}
                  </td>
                );
              })
            )}
          </tr>
        );
      })}
    </>
  );
}
export const SubRowAsync = ({ row, rowProps, visibleColumns, renderAssets, t }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setData([row.original]);
      setLoading(false);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <SubRows
      row={row}
      rowProps={rowProps}
      visibleColumns={visibleColumns}
      data={data}
      loading={loading}
      renderAssets={renderAssets}
      t={t}
    />
  );
};
// Our table component
// skipPageResetRef prop is expected to be given and used like here: https://react-table.tanstack.com/docs/faq#how-do-i-stop-my-table-state-from-automatically-resetting-when-my-data-changes
export const Table = ({
  columns,
  data,
  useTableGlobalFilter,
  t,
  tableClass,
  isLazyLoading,
  renderRowSubComponent,
  defaultFilteredFields,
  defaultGlobalFieldValue,
  isRemovingFilters,
  setGlobalFilterValue,
  hiddenColumns,
  skipPageResetRef = {},
}) => {
  // After the table has updated, always remove the flag
  useEffect(() => (skipPageResetRef.current = false));
  const filterTypes = useMemo(
    () => ({
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
      select: (rows, id, filterValue) => {
        if (filterValue === undefined || filterValue === null || filterValue === "") {
          return rows;
        }

        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue).toLowerCase() === String(filterValue).toLowerCase()
            : true;
        });
      },
    }),
    []
  );

  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      /* Using function-syntax here to go around an eslint warning:
         "Component definition is missing display name. eslint(react/display-name)"
      */
      Filter: function filter(props) {
        return <DefaultColumnFilter {...props} t={t} />;
      },
      minWidth: 80,
      width: 160,
      maxWidth: 400,
    }),
    [t]
  );
  //const initialState = { hiddenColumns: ["assetSender"] };
  const {
    getTableProps,
    getTableBodyProps,
    visibleColumns,
    headerGroups,
    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    setAllFilters,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      initialState: { pageIndex: 0, hiddenColumns: hiddenColumns || [] },
      autoResetExpanded: !skipPageResetRef.current,
      autoResetPage: !skipPageResetRef.current,
      autoResetFilters: !skipPageResetRef.current,
    },
    useFilters,
    useGlobalFilter,
    useExpanded,
    usePagination,
    useFlexLayout
  );

  useEffect(() => {
    if (defaultFilteredFields?.length > 0) {
      setAllFilters(defaultFilteredFields);
    } else if (defaultFilteredFields?.length <= 0) {
      setAllFilters([]);
    }
    setGlobalFilter(defaultGlobalFieldValue);
  }, [defaultFilteredFields, defaultGlobalFieldValue, setAllFilters]);
  return (
    <div className={`react-table ${tableClass ? tableClass : ""}`}>
      {useTableGlobalFilter && (
        <GlobalFilter
          isLazyLoading={isLazyLoading}
          defaultGlobalFieldValue={defaultGlobalFieldValue}
          preGlobalFilteredRows={preGlobalFilteredRows}
          setGlobalFilterValue={setGlobalFilterValue}
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
          isRemovingFilters={isRemovingFilters}
          t={t}
        />
      )}
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup, i) => (
            <tr key={i} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, i) => (
                <th key={i} {...column.getHeaderProps()}>
                  {column.render("Header")}
                  {/* Render the columns filter UI */}
                  <div>{column.canFilter ? column.render("Filter") : null}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            const rowProps = row.getRowProps();
            return (
              // Use a React.Fragment here so the table markup is still valid
              <Fragment key={rowProps.key}>
                <tr {...rowProps} className={row.isExpanded ? "expanded" : ""}>
                  {row.cells.map((cell, c) => {
                    return (
                      <td key={c} {...cell.getCellProps()}>
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
                {/* We could pass anything into this */}
                {row.isExpanded && renderRowSubComponent({ row, rowProps, visibleColumns, t })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <div className="pagination">
        <Icon
          name="angle double left"
          role="button"
          onClick={() => gotoPage(0)}
          disabled={!canPreviousPage}
        />
        <Icon
          name="angle left"
          role="button"
          onClick={() => previousPage()}
          disabled={!canPreviousPage}
        />
        <Icon name="angle right" role="button" onClick={() => nextPage()} disabled={!canNextPage} />
        <Icon
          name="angle double right"
          role="button"
          onClick={() => gotoPage(pageCount - 1)}
          disabled={!canNextPage}
        />
        <span>
          {`${t("page")} `}
          <strong>{`${pageIndex + 1} / ${pageOptions.length}`}</strong>{" "}
        </span>
        <span>
          {`| ${t("go_to_page")}: `}
          <input
            type="number"
            defaultValue={pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? parseInt(e.target.value) - 1 : 0;
              gotoPage(page);
            }}
            style={{ width: "100px" }}
          />
        </span>{" "}
        {/* The following eslint warning is disabled since using onBlur doesn't achieve the
            desired funcionality:
            "onBlur must be used instead of onchange, unless absolutely necessary 
            and it causes no negative consequences for keyboard only or screen reader users." 
        */}
        {/* eslint-disable-next-line jsx-a11y/no-onchange */}
        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(parseInt(e.target.value));
          }}
        >
          {[10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              {`${t("show")} ${pageSize}`}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
